import React from 'react';
/**
 * `PageInfoPlanSummary` renders a compact summary of the currently selected
 * products for display in the sidebar.
 *
 * For each selected product it shows:
 * - payment method (translated)
 * - policy start date (culture-formatted)
 * - duration or "until terminated" for subscriptions
 * - a `PriceDisplay` for the selected variant
 */

import {
  getSelectedProducts,
  getSelectedVariant
} from '../common/ProductMethods';
import { KeyValuePair, ProductType } from '../common/CommonTypes';
import {
  getFormattedDate,
  getSelectedNoOfInstallments,
  getPaymentMethodDisplayName
} from '../common/HelperMethods';
import PriceDisplay from '../common/PriceDisplay';
import { SummaryItem } from '../common/Summary/Summary';
import { PaymentMethod } from '../common/Constants';
import { getDateStringByBrowserLanguage } from '../common/Formatter';
import { useTranslation } from 'react-i18next';

export type PageInfoPlanSummaryProps = {
  products: ProductType[];
  culture: string;
};

const PageInfoPlanSummary = ({
  products,
  culture
}: PageInfoPlanSummaryProps): JSX.Element => {
  const selectedProducts = getSelectedProducts(products);

  const { t } = useTranslation();

  let lineCount = 0;

  const renderLine = () => {
    if (lineCount < selectedProducts.length - 1) {
      lineCount += 1;
      return <hr />;
    }
  };

  const paymentMethodDisplayNames: KeyValuePair[] = [
    {
      key: PaymentMethod.Single,
      value: t('customer-product-page.payment-type.single')
    },
    {
      key: PaymentMethod.Monthly,
      value: t('customer-product-page.payment-type.monthly')
    },
    {
      key: PaymentMethod.Subscription,
      value: t('customer-product-page.payment-type.subscription')
    }
  ];

  return (
    <div className='plan-summary'>
      <p className='p1-bold m1-bottom'>
        {t('page-info.plan-summary.customer-plan-label')}
      </p>
      {selectedProducts.map((product: ProductType) => {
        const selectedVariant = getSelectedVariant(product.productVariants);
        let policyStartDate = '';
        if (product.policyStartDate !== undefined) {
          policyStartDate = getFormattedDate(product.policyStartDate);
          policyStartDate = getDateStringByBrowserLanguage(
            culture,
            policyStartDate
          );
        }
        if (selectedVariant === undefined) {
          return <></>;
        }

        return (
          <div key={`plan-summary-${product.productTypeId}`}>
            <span className='p3 bold'>
              {selectedVariant?.customerFriendlyName ?? product.productName}
            </span>
            <div className='space-between m05-bottom'>
              <div className='flex-column info-summary'>
                <div className='p3'>
                  <SummaryItem
                    label={t('page-info.plan-summary.payment-label')}
                    value={getPaymentMethodDisplayName(
                      selectedVariant.paymentMethod,
                      paymentMethodDisplayNames
                    )}
                  />
                </div>
                <div className='p3'>
                  <SummaryItem
                    label={t('page-info.plan-summary.start-date-label')}
                    value={policyStartDate}
                  />
                </div>
                <div className='m1-bottom p3'>
                  <SummaryItem
                    label={t('page-info.plan-summary.duration-label')}
                    value={
                      selectedVariant.paymentMethod ===
                      PaymentMethod.Subscription
                        ? t('page-info.plan-summary.until-terminated-text')
                        : t('page-info.plan-summary.duration-text', {
                            duration: selectedVariant.duration
                          })
                    }
                  />
                </div>
              </div>
              <div className='flex-column info-summary-price'>
                <PriceDisplay
                  product={product}
                  noOfInstallments={getSelectedNoOfInstallments(product)}
                  className='p3'
                  selectedLinkedProducts={[]}
                  culture={culture}
                />
              </div>
            </div>
            {renderLine()}
          </div>
        );
      })}
    </div>
  );
};

export default PageInfoPlanSummary;
